import { Fragment, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronLeft, ChevronRight, Pencil, RefreshCw, Trash2 } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { CardCifra } from '../../components/ui/CardCifra'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import {
  ListaDocumente,
  STARE,
  citesteSuma,
  lei,
  mesajEroare,
  zi,
  type DocLista,
} from './taxeComune'
import { ImpozitProfit, trimestruImplicit } from './TaxeProfit'

/*
  Bani → Taxe & impozite (Alexandru, 23.10.2026). Faza 1: TVA de plata pe luna, estimata din
  copia notelor contabile din ExpertAccounts si din datele CRM (SPV, extrase, deconturi).
  Faza 2: impozitul pe profit trimestrial (`TaxeProfit.tsx`). Doar adminii — serverul refuza pe
  ceilalti.
*/

interface Componenta {
  cod: 'A' | 'B1' | 'B2' | 'B3' | 'B4' | 'B5'
  eticheta: string
  sursa: string
  tip: 'contabilitate' | 'estimat'
  total: number
  documente: DocLista[]
  deVerificat?: DocLista[]
  nota?: string
}
interface Ajustare {
  id: number
  descriere: string
  suma: number
  cont: string | null
  autor: string
  data: string
}
interface StareCopie {
  stare: 'in_curs' | 'ok' | 'eroare'
  pornitLa: string
  terminatLa?: string
  intervale: { de: string; pana: string; randuri: number; sterse: number }[]
  eroare?: string
}
interface Copie {
  stare: StareCopie | null
  ultimaCopiere: string | null
  randuri: number
  inCurs?: boolean
}
interface Estimare {
  perioada: string
  termen: string
  deplata: number
  stare: 'estimat' | 'declarat' | 'platit'
  componente: Componenta[]
  ajustari: Ajustare[]
  totalAjustari: number
  declarat: {
    sumaDeclarata: number | null
    sumaPlatita: number | null
    dataPlata: string | null
    note: string | null
    actualizatDe: string | null
    actualizatLa: string
  } | null
  diferenta: number | null
  copie: Copie
}

const LUNI = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
]
const numeLuna = (p: string) => `${LUNI[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`
function mutaLuna(p: string, cu: number): string {
  const d = new Date(Date.UTC(Number(p.slice(0, 4)), Number(p.slice(5, 7)) - 1 + cu, 1))
  return d.toISOString().slice(0, 7)
}
/** Luna implicita: cea precedenta — pe ea se depune decontul de TVA luna asta. */
function lunaImplicita(): string {
  const a = new Date()
  return mutaLuna(`${a.getFullYear()}-${String(a.getMonth() + 1).padStart(2, '0')}`, -1)
}

const SEMN: Record<Componenta['cod'], string> = {
  A: '+',
  B1: '−',
  B2: '−',
  B3: '−',
  B4: '−',
  B5: '−',
}

type Obligatie = 'tva' | 'profit'

export function TaxeImpozitePage() {
  const [param, setParam] = useSearchParams()
  const obligatie: Obligatie = param.get('obligatie') === 'profit' ? 'profit' : 'tva'
  const trimestru = /^\d{4}-T[1-4]$/.test(param.get('trimestru') ?? '')
    ? param.get('trimestru')!
    : trimestruImplicit()
  const seteaza = (k: string, v: string) => {
    const n = new URLSearchParams(param)
    n.set(k, v)
    setParam(n, { replace: true })
  }
  const perioada = /^\d{4}-\d{2}$/.test(param.get('luna') ?? '')
    ? param.get('luna')!
    : lunaImplicita()
  const setPerioada = (p: string) => seteaza('luna', p)
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const qc = useQueryClient()

  const q = useQuery<Estimare>({
    queryKey: ['taxe-tva', perioada],
    queryFn: async () => (await api.get('/taxe/tva', { params: { perioada } })).data,
    enabled: obligatie === 'tva',
  })
  const copie = useQuery<Copie>({
    queryKey: ['taxe-copie'],
    queryFn: async () => (await api.get('/taxe/copie')).data,
    refetchInterval: (query) => (query.state.data?.inCurs ? 5000 : false),
  })
  // Cand copierea se termina, estimarea se reface din copia noua.
  const inCurs = !!copie.data?.inCurs
  const [eraInCurs, setEraInCurs] = useState(false)
  useEffect(() => {
    if (eraInCurs && !inCurs) {
      void qc.invalidateQueries({ queryKey: ['taxe-tva'] })
      void qc.invalidateQueries({ queryKey: ['taxe-profit'] })
    }
    setEraInCurs(inCurs)
  }, [inCurs, eraInCurs, qc])

  const reimprospateaza = useMutation({
    mutationFn: async () =>
      (await api.post('/taxe/copie', { perioada: obligatie === 'profit' ? trimestru : perioada }))
        .data,
    onSuccess: () => {
      toast.success(
        `Am pornit citirea notelor pe ${
          obligatie === 'profit'
            ? `trimestrul ${trimestru.slice(6)} ${trimestru.slice(0, 4)}`
            : numeLuna(perioada)
        } din ExpertAccounts. Durează câteva minute.`,
      )
      void qc.invalidateQueries({ queryKey: ['taxe-copie'] })
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const d = q.data
  const c = copie.data ?? d?.copie
  const stareCopie = c?.stare

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Taxe & impozite"
        descriere="Cât are de plătit firma la buget, estimat din notele contabile din ExpertAccounts și din documentele încă neînregistrate: TVA lunar și impozitul pe profit trimestrial."
        actiuni={
          eAdmin && (
            <Button
              variant="secondary"
              onClick={() => reimprospateaza.mutate()}
              loading={reimprospateaza.isPending || inCurs}
            >
              <RefreshCw size={14} className="mr-1.5" />
              Reîmprospătează din EA
            </Button>
          )
        }
      />

      <ChipuriFiltru<Obligatie>
        eticheta="Obligația"
        chipuri={[
          { cheie: 'tva', eticheta: 'TVA' },
          { cheie: 'profit', eticheta: 'Impozit pe profit' },
        ]}
        ales={obligatie}
        onAlege={(o) => seteaza('obligatie', o)}
      />

      {obligatie === 'tva' && (
        <div className="flex flex-wrap items-center gap-2 text-corp">
          <Button variant="secondary" onClick={() => setPerioada(mutaLuna(perioada, -1))}>
            <ChevronLeft size={14} />
            <span className="sr-only">Luna precedentă</span>
          </Button>
          <input
            type="month"
            value={perioada}
            onChange={(e) => e.target.value && setPerioada(e.target.value)}
            aria-label="Luna"
            className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-corp"
          />
          <Button variant="secondary" onClick={() => setPerioada(mutaLuna(perioada, 1))}>
            <ChevronRight size={14} />
            <span className="sr-only">Luna următoare</span>
          </Button>
          <span className="text-b4l-text-muted">TVA pe {numeLuna(perioada)}</span>
        </div>
      )}

      <div className="rounded-caseta border border-b4l-amber bg-b4l-amber-light px-3.5 py-2.5 text-corp text-b4l-amber-dark">
        <strong>Estimare — nu înlocuiește declarația.</strong>{' '}
        {c?.ultimaCopiere
          ? `Ultima copie a notelor din ExpertAccounts: ${new Date(c.ultimaCopiere).toLocaleString('ro-RO')} (${c.randuri.toLocaleString('ro-RO')} linii).`
          : 'Notele contabile nu au fost încă copiate din ExpertAccounts.'}
        {inCurs && ' Citirea din ExpertAccounts e în curs…'}
        {stareCopie?.stare === 'eroare' && (
          <span className="block">Ultima citire a eșuat: {stareCopie.eroare}</span>
        )}
      </div>

      {obligatie === 'profit' ? (
        <ImpozitProfit
          trimestru={trimestru}
          setTrimestru={(t) => seteaza('trimestru', t)}
          eAdmin={eAdmin}
        />
      ) : (
        <StareLista interogare={q} gol={false} mesajGol="">
          {d && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <CardCifra
                  ton={d.deplata >= 0 ? 'albastru' : 'verde'}
                  eticheta={d.deplata >= 0 ? 'TVA de plată' : 'TVA de recuperat'}
                  valoare={`${lei(d.deplata)} lei`}
                  detaliu={
                    <>
                      Termen: {zi(d.termen)} ·{' '}
                      <Pastila ton={STARE[d.stare].ton}>{STARE[d.stare].eticheta}</Pastila>
                    </>
                  }
                  explicatie="A − B1 − B2 − B3 − B4 − B5 + ajustări. Termenul e 25 ale lunii următoare, mutat pe prima zi lucrătoare."
                />
                <CardCifra
                  ton="gri"
                  eticheta="Declarat (D300)"
                  valoare={
                    d.declarat?.sumaDeclarata != null ? `${lei(d.declarat.sumaDeclarata)} lei` : '—'
                  }
                  detaliu={
                    d.declarat?.sumaPlatita != null
                      ? `Plătit ${lei(d.declarat.sumaPlatita)} lei${d.declarat.dataPlata ? ` la ${zi(d.declarat.dataPlata)}` : ''}`
                      : 'Neplătit încă'
                  }
                />
                <CardCifra
                  ton={d.diferenta == null ? 'gri' : Math.abs(d.diferenta) < 1 ? 'verde' : 'galben'}
                  eticheta="Estimat − declarat"
                  valoare={d.diferenta == null ? '—' : `${lei(d.diferenta)} lei`}
                  detaliu={
                    d.diferenta == null ? 'Apare după ce completezi suma declarată.' : undefined
                  }
                />
              </div>

              <TabelComponente d={d} />
              {eAdmin && <Ajustari perioada={perioada} ajustari={d.ajustari} />}
              {eAdmin && <Declarat perioada={perioada} d={d} />}
            </>
          )}
        </StareLista>
      )}
    </div>
  )
}

function TabelComponente({ d }: { d: Estimare }) {
  const [deschis, setDeschis] = useState<string | null>(null)
  return (
    <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
      <table className="w-full min-w-[760px] border-collapse text-corp">
        <thead>
          <tr className="text-left text-mic text-b4l-text-muted">
            <th className="border-b border-b4l-border px-3.5 py-2.5 font-medium">Rând</th>
            <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Ce</th>
            <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Sursa</th>
            <th className="border-b border-b4l-border px-3.5 py-2.5 text-right font-medium">
              Suma (lei)
            </th>
          </tr>
        </thead>
        <tbody>
          {d.componente.map((c) => {
            const e = deschis === c.cod
            const arePeCe = c.documente.length > 0 || !!c.deVerificat?.length || !!c.nota
            return (
              <Fragment key={c.cod}>
                <tr
                  onClick={() => arePeCe && setDeschis(e ? null : c.cod)}
                  className={arePeCe ? 'cursor-pointer hover:bg-b4l-bg-soft' : ''}
                >
                  <td className="border-b border-b4l-border px-3.5 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      {arePeCe && (
                        <ChevronDown
                          size={14}
                          className={`transition ${e ? '' : '-rotate-90'}`}
                          aria-hidden
                        />
                      )}
                      {c.cod}
                    </span>
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    {c.eticheta}{' '}
                    <Pastila ton={c.tip === 'contabilitate' ? 'gri' : 'mov'}>
                      {c.tip === 'contabilitate' ? 'din contabilitate' : 'estimat din CRM'}
                    </Pastila>
                    {!!c.deVerificat?.length && (
                      <span className="ml-1">
                        <Pastila ton="galben">{c.deVerificat.length} de verificat</Pastila>
                      </span>
                    )}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2 text-mic text-b4l-text-muted">
                    {c.sursa}
                  </td>
                  <td className="border-b border-b4l-border px-3.5 py-2 text-right tabular-nums">
                    {SEMN[c.cod]} {lei(c.total)}
                  </td>
                </tr>
                {e && (
                  <tr>
                    <td
                      colSpan={4}
                      className="border-b border-b4l-border bg-b4l-bg-soft px-3.5 py-3"
                    >
                      {c.nota && <p className="m-0 mb-2 text-mic text-b4l-text-muted">{c.nota}</p>}
                      {c.documente.length > 0 && <ListaDocumente docs={c.documente} />}
                      {!!c.deVerificat?.length && (
                        <div className="mt-3">
                          <p className="m-0 mb-1 text-mic font-semibold text-b4l-amber-dark">
                            De verificat — nu intră în sumă
                          </p>
                          <ListaDocumente docs={c.deVerificat} />
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          <tr>
            <td className="border-b border-b4l-border px-3.5 py-2 font-medium">±</td>
            <td className="border-b border-b4l-border px-2 py-2">
              Ajustări manuale <Pastila ton="galben">manual</Pastila>
            </td>
            <td className="border-b border-b4l-border px-2 py-2 text-mic text-b4l-text-muted">
              Introduse mai jos, cu explicație
            </td>
            <td className="border-b border-b4l-border px-3.5 py-2 text-right tabular-nums">
              {d.totalAjustari >= 0 ? '+' : '−'} {lei(Math.abs(d.totalAjustari))}
            </td>
          </tr>
          <tr className="font-semibold">
            <td className="px-3.5 py-2.5" colSpan={3}>
              {d.deplata >= 0 ? 'TVA de plată' : 'TVA de recuperat'}
            </td>
            <td className="px-3.5 py-2.5 text-right tabular-nums">{lei(d.deplata)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function Ajustari({ perioada, ajustari }: { perioada: string; ajustari: Ajustare[] }) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [editez, setEditez] = useState<number | null>(null)
  const [descriere, setDescriere] = useState('')
  const [suma, setSuma] = useState('')
  const [cont, setCont] = useState('')
  const goleste = () => {
    setEditez(null)
    setDescriere('')
    setSuma('')
    setCont('')
  }
  const reincarca = () => qc.invalidateQueries({ queryKey: ['taxe-tva', perioada] })
  const salveaza = useMutation({
    mutationFn: async () => {
      const s = citesteSuma(suma)
      if (s == null || Number.isNaN(s)) throw new Error('Suma nu e un număr.')
      const body = { descriere: descriere.trim(), suma: s, cont: cont.trim() || null }
      if (editez != null) await api.put(`/taxe/ajustari/${editez}`, body)
      else await api.post('/taxe/ajustari', { ...body, perioada })
    },
    onSuccess: () => {
      toast.success(editez != null ? 'Ajustarea a fost modificată.' : 'Ajustarea a fost adăugată.')
      goleste()
      void reincarca()
    },
    onError: (e) =>
      toast.error(e instanceof Error && !('response' in e) ? e.message : mesajEroare(e)),
  })
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/taxe/ajustari/${id}`),
    onSuccess: () => {
      toast.success('Ajustarea a fost ștearsă.')
      void reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  return (
    <section className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
      <div>
        <h2 className="m-0 text-base font-semibold">Ajustări manuale</h2>
        <p className="m-0 text-mic text-b4l-text-muted">
          Judecată contabilă care nu reiese din documente. Suma se adună la TVA de plată: pozitivă o
          crește, negativă o scade.
        </p>
      </div>
      {ajustari.length > 0 && (
        <ul className="m-0 grid gap-2 p-0">
          {ajustari.map((a) => (
            <li
              key={a.id}
              className="flex list-none flex-wrap items-center gap-2 rounded-caseta border border-b4l-border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{a.descriere}</div>
                <div className="text-mic text-b4l-text-muted">
                  {a.cont ? `cont ${a.cont} · ` : ''}
                  {a.autor}, {new Date(a.data).toLocaleString('ro-RO')}
                </div>
              </div>
              <span className="tabular-nums">{lei(a.suma)} lei</span>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditez(a.id)
                  setDescriere(a.descriere)
                  setSuma(String(a.suma).replace('.', ','))
                  setCont(a.cont ?? '')
                }}
              >
                <Pencil size={14} />
                <span className="sr-only">Modifică</span>
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  if (
                    await confirma({
                      titlu: 'Ștergi ajustarea?',
                      mesaj: a.descriere,
                      buton: 'Șterge ajustarea',
                      pericol: true,
                    })
                  )
                    sterge.mutate(a.id)
                }}
              >
                <Trash2 size={14} />
                <span className="sr-only">Șterge</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="grid gap-2 sm:grid-cols-[1fr_140px_120px_auto]"
        onSubmit={(e) => {
          e.preventDefault()
          salveaza.mutate()
        }}
      >
        <input
          value={descriere}
          onChange={(e) => setDescriere(e.target.value)}
          placeholder="Explicație (ex. TVA nedeductibilă protocol)"
          aria-label="Explicație"
          className="rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp"
        />
        <input
          value={suma}
          onChange={(e) => setSuma(e.target.value)}
          placeholder="Suma (lei)"
          aria-label="Suma"
          inputMode="decimal"
          className="rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-right text-corp tabular-nums"
        />
        <input
          value={cont}
          onChange={(e) => setCont(e.target.value)}
          placeholder="Cont (opțional)"
          aria-label="Cont"
          className="rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp"
        />
        <div className="flex gap-2">
          <Button
            type="submit"
            loading={salveaza.isPending}
            disabled={descriere.trim().length < 3 || !suma.trim()}
          >
            {editez != null ? 'Salvează' : 'Adaugă'}
          </Button>
          {editez != null && (
            <Button variant="secondary" onClick={goleste}>
              Renunță
            </Button>
          )}
        </div>
      </form>
    </section>
  )
}

function Declarat({ perioada, d }: { perioada: string; d: Estimare }) {
  const qc = useQueryClient()
  const [declarata, setDeclarata] = useState('')
  const [platita, setPlatita] = useState('')
  const [dataPlata, setDataPlata] = useState('')
  const [note, setNote] = useState('')
  useEffect(() => {
    const x = d.declarat
    setDeclarata(x?.sumaDeclarata != null ? String(x.sumaDeclarata).replace('.', ',') : '')
    setPlatita(x?.sumaPlatita != null ? String(x.sumaPlatita).replace('.', ',') : '')
    setDataPlata(x?.dataPlata ?? '')
    setNote(x?.note ?? '')
  }, [d.declarat, perioada])

  const salveaza = useMutation({
    mutationFn: async () => {
      const sd = citesteSuma(declarata)
      const sp = citesteSuma(platita)
      if (Number.isNaN(sd) || Number.isNaN(sp)) throw new Error('Sumele trebuie să fie numere.')
      await api.put(`/taxe/declarat/tva/${perioada}`, {
        sumaDeclarata: sd,
        sumaPlatita: sp,
        dataPlata: dataPlata || null,
        note: note.trim() || null,
      })
    },
    onSuccess: () => {
      toast.success('Sumele declarate au fost salvate.')
      void qc.invalidateQueries({ queryKey: ['taxe-tva', perioada] })
    },
    onError: (e) =>
      toast.error(e instanceof Error && !('response' in e) ? e.message : mesajEroare(e)),
  })

  const camp =
    'rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp tabular-nums'
  return (
    <section className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
      <div>
        <h2 className="m-0 text-base font-semibold">Declarat și plătit</h2>
        <p className="m-0 text-mic text-b4l-text-muted">
          După depunerea D300: suma din declarație și plata la buget. Starea devine „declarat”, apoi
          „plătit”, iar diferența față de estimare apare sus.
          {d.declarat?.actualizatDe &&
            ` Ultima modificare: ${d.declarat.actualizatDe}, ${new Date(d.declarat.actualizatLa).toLocaleString('ro-RO')}.`}
        </p>
      </div>
      <form
        className="grid gap-2 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault()
          salveaza.mutate()
        }}
      >
        <label className="grid gap-1 text-mic text-b4l-text-muted">
          Suma declarată (lei)
          <input
            value={declarata}
            onChange={(e) => setDeclarata(e.target.value)}
            inputMode="decimal"
            className={`${camp} text-right`}
          />
        </label>
        <label className="grid gap-1 text-mic text-b4l-text-muted">
          Suma plătită (lei)
          <input
            value={platita}
            onChange={(e) => setPlatita(e.target.value)}
            inputMode="decimal"
            className={`${camp} text-right`}
          />
        </label>
        <label className="grid gap-1 text-mic text-b4l-text-muted">
          Data plății
          <input
            type="date"
            value={dataPlata}
            onChange={(e) => setDataPlata(e.target.value)}
            className={camp}
          />
        </label>
        <label className="grid gap-1 text-mic text-b4l-text-muted">
          Note
          <input value={note} onChange={(e) => setNote(e.target.value)} className={camp} />
        </label>
        <div className="sm:col-span-4">
          <Button type="submit" loading={salveaza.isPending}>
            Salvează
          </Button>
        </div>
      </form>
    </section>
  )
}
